import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * The active search term for a region of the tree.
 *
 * Why a context: a list page keeps its query in one place (Redux, a URL param, a
 * `useState`) but the text that needs marking is many layers down — inside a
 * column factory, a group header, a card body. Threading a `query` prop through
 * every one of those is the reason most tables never got highlighting at all.
 * With this, a page wires the query ONCE and every `<HighlightText>` beneath it
 * picks it up.
 *
 * An explicit `query` prop on `HighlightText` still wins, so a nested surface
 * with its own search box (a picker inside a page) can opt out of the page's
 * term without unmounting the provider.
 */
const SearchHighlightContext = createContext<string>('');

export function useSearchHighlightQuery(): string {
  return useContext(SearchHighlightContext);
}

export interface SearchHighlightProviderProps {
  /** The active search term. Empty / null disables highlighting beneath it. */
  query?: string | null;
  children: ReactNode;
}

/**
 * Publishes `query` to every `<HighlightText>` below it.
 *
 * `DataTable` mounts this itself from its `searchQuery` prop — reach for the
 * provider directly only for non-table surfaces (card grids, sidebar panels,
 * master lists).
 */
export function SearchHighlightProvider({ query, children }: SearchHighlightProviderProps) {
  // `undefined` means "not specified" and INHERITS the surrounding term. This
  // matters because DataTable mounts a provider unconditionally: without the
  // pass-through, dropping a table with no `searchQuery` inside a page-level
  // provider would silently blank out the page's term for every cell in it.
  // An explicit `null` / '' still clears it — that is the deliberate opt-out.
  const inherited = useContext(SearchHighlightContext);
  const value = useMemo(
    () => (query === undefined ? inherited : (query ?? '')).trim(),
    [query, inherited],
  );
  return (
    <SearchHighlightContext.Provider value={value}>{children}</SearchHighlightContext.Provider>
  );
}
